/**
 * Fórmula de dano central e reutilizável.
 *   FinalDamage = BaseDamage           (normal)
 *   FinalDamage = BaseDamage × CritMult (crítico)
 * 10.5K — DamagePacket completo: o atacante tem +% por TIPO (cortante/perfurante/contundente),
 * o alvo tem armadura, resiliência, DEFESA por tipo e chance de BLOQUEIO.
 *   final = roll × (1+typeBonus) × (1-armorRed) × (1-resil) × (1-typeDef) × (bloqueou ? 1-blockRed : 1)
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { events } from "../core/Events";
import type { CombatStats } from "./CombatStats";
import type { Damageable } from "./Damageable";
import type { DamageType, WeaponType } from "../items/EquipmentTypes";

export interface DamageResult {
  amount: number;
  crit: boolean;
  killed: boolean;
  blocked: boolean;
  /** 10.6G — o golpe usou a arma que o alvo TEME (+dano) ou a que ele aguenta (-dano). */
  weak: boolean;
  resisted: boolean;
}

export interface DamageOptions {
  targetKind: "player" | "enemy";
  damageType?: DamageType; // tipo do golpe (aplica bônus do atacante e defesa do alvo)
  /** 10.6G — ARMA usada. Cada monstro tem uma arma que o machuca mais e uma que ele aguenta. */
  weaponType?: WeaponType;
  /** 10.7N — rank do ATACANTE: elite/campeão/boss furam a defesa do player com dano % do HP máx. */
  attackerRank?: "elite" | "champion" | "boss";
  knockbackFrom?: THREE.Vector3;
  knockbackForce?: number;
  /** 10.9EQ — golpe DO CAÇADOR (ataque, flecha, Corrente, magia). Servos do necromante nao contam. */
  byPlayer?: boolean;
}

const _dir = new THREE.Vector3();

export function rollDamage(stats: CombatStats): { amount: number; crit: boolean } {
  const crit = Math.random() < stats.critChance;
  const raw = crit ? stats.baseDamage * stats.critDamageMult : stats.baseDamage;
  return { amount: Math.round(raw), crit };
}

/** Redução de armadura (fórmula única: armor/(armor+100)). */
export function armorReduction(armor: number): number {
  return armor > 0 ? armor / (armor + 100) : 0;
}

export function dealDamage(target: Damageable, stats: CombatStats, opts: DamageOptions): DamageResult {
  if (target.isDead || target.invulnerable) return { amount: 0, crit: false, killed: false, blocked: false, weak: false, resisted: false };

  const { amount: rolled, crit } = rollDamage(stats);
  const t = opts.damageType;
  // bônus do ATACANTE por tipo de dano
  const typeBonus = t && stats.typeBonus ? stats.typeBonus[t] ?? 0 : 0;
  let amount = rolled * (1 + Math.max(-0.9, typeBonus));

  // armadura do alvo: redução = armor/(armor+100); dano final nunca < 1
  const reduction = armorReduction(target.armor ?? 0);
  const resil = Math.min(0.8, Math.max(0, target.damageReduction ?? 0)); // Void Tree
  const cc = GameConfig.combat;
  const typeDef = t && target.typeDefense ? Math.min(cc.typeDefenseCap, Math.max(0, target.typeDefense[t] ?? 0)) : 0;
  const blocked = (target.blockChance ?? 0) > 0 && Math.random() < Math.min(cc.blockChanceCap, target.blockChance ?? 0);
  const beforeMitigation = amount; // dano após typeBonus, antes das reduções
  amount *= (1 - reduction) * (1 - resil) * (1 - typeDef) * (blocked ? 1 - cc.blockReduction : 1);
  // 10.7G — ANTI-IMORTALIDADE: as reduções multiplicavam até ~98%. O PLAYER sempre toma
  // pelo menos (1 - maxTotalReduction) do golpe bruto. Monstros não têm esse piso (o design
  // deles é resistir por arma/tipo). Corrige o "não tomo mais dano" com árvore/altar cheios.
  const asPlayer = opts.targetKind === "player" && !target.decoy; // 10.9CM — servo não é o player
  if (asPlayer) {
    const floor = beforeMitigation * (1 - (cc.maxTotalReduction ?? 0.8));
    if (amount < floor) amount = floor;
    // 10.7N — dano PERCENTUAL de elite/campeão/boss: fura toda defesa. Ameaça o tank que
    // mata tudo rápido e a regen cobria o resto (era a raiz da imortalidade que sobrava).
    if (opts.attackerRank) {
      const pct = opts.attackerRank === "boss" ? (cc.bossPctMaxHp ?? 0.1)
        : opts.attackerRank === "champion" ? (cc.championPctMaxHp ?? 0.07)
        : (cc.elitePctMaxHp ?? 0.04);
      const maxHp = target.health?.max ?? 0;
      amount += maxHp * pct;
    }
    // 10.9EW — PELE DE ACO (classe): aplicada DEPOIS do piso e do dano % de elite. Antes entrava junto
    // da resiliencia e o piso de 20% + o dano % anulavam quase tudo (35% de reducao virava ~5%).
    const cdr = Math.min(0.6, Math.max(0, target.classDamageReduction ?? 0));
    if (cdr > 0) amount *= 1 - cdr;
  }
  // 10.6G — FRAQUEZA / RESISTÊNCIA POR ARMA. É o que dá sentido a trocar de arma por
  // território: cada monstro teme uma arma e aguenta outra.
  const wt = opts.weaponType;
  const weak = !!wt && target.weakTo === wt;
  const resisted = !!wt && !weak && target.resists === wt;
  const toughened = !!wt && !weak && !resisted && target.tough === wt;
  if (weak) amount *= 1 + cc.weakBonus;
  else if (resisted) amount *= 1 - cc.resistCut;
  else if (toughened) amount *= 1 - cc.toughCut;
  const final = Math.max(1, Math.round(amount));
  target.health.damage(final);
  target.onHit?.();

  if (opts.knockbackFrom && opts.knockbackForce && target.applyKnockback) {
    _dir.subVectors(target.position, opts.knockbackFrom);
    _dir.y = 0;
    if (_dir.lengthSq() > 1e-6) {
      _dir.normalize();
      // bloqueio segura o corpo: metade do empurrão
      target.applyKnockback(_dir, blocked ? opts.knockbackForce * 0.5 : opts.knockbackForce);
    }
  }

  events.emit("damage:dealt", {
    amount: final,
    crit,
    position: target.position.clone(),
    target: asPlayer ? "player" : "enemy", // 10.9CM — servo atingido conta como "enemy" (não treme a tela)
    damageType: opts.damageType,
    blocked,
    weak,
    resisted: resisted || toughened,
    byPlayer: !!opts.byPlayer, // 10.9EQ — roubo de vida
  });

  return { amount: final, crit, killed: target.isDead, blocked, weak, resisted: resisted || toughened };
}
